import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChatCircleDots, X, PaperPlaneRight } from "@phosphor-icons/react";
import { ChatText, TimeChoices } from "./ChatParts";
import { ChatBookingForm } from "./ChatBookingForm";

const API = process.env.REACT_APP_BACKEND_URL;
const HIDDEN = ["/admin", "/dashboard", "/login", "/register", "/mfa-setup", "/reset-password", "/forgot-password", "/payment", "/complete-payment", "/pay/"];
const HELLO = { role: "assistant", text: "Hello! I'm the clinic's **AI assistant** - an automated computer program, not a person. I can answer questions about the clinic and our tests, and help you book an appointment. How can I help? (You can write in any language.)" };

async function streamChat(body, onDelta) {
  const res = await fetch(`${API}/api/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Sorry, something went wrong.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", done = null;
  for (;;) {
    const { value, done: end } = await reader.read();
    if (end) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const d = JSON.parse(p.slice(6));
      if (d.t === "delta") onDelta(d.text);
      if (d.t === "done") done = d;
    }
  }
  return done?.actions || [];
}

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([HELLO]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState(null);
  const [ended, setEnded] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, choice]);
  if (HIDDEN.some((p) => pathname.startsWith(p))) return null;

  const send = async (e) => {
    e.preventDefault();
    const message = text.trim();
    if (!message || busy || ended) return;
    setText(""); setBusy(true); setChoice(null);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const history = msgs.slice(1).filter((m) => m.text).map(({ role, text: t }) => ({ role, text: t }));
      const actions = await streamChat({ message, history }, (t) => patch((x) => ({ ...x, text: x.text + t })));
      patch((x) => ({ ...x, actions }));
      if (actions.some((a) => a.type === "ended")) setEnded(true);
    } catch (ex) {
      patch((x) => ({ ...x, text: x.text || ex.message }));
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="chat-launcher"
        className="fixed bottom-5 right-5 z-[55] inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-bronze-dark text-white shadow-xl hover:bg-ink transition-colors">
        <ChatCircleDots size={22} weight="fill" /> <span className="text-sm font-medium">Ask our AI assistant</span>
      </button>
    );
  }
  return (
    <div data-testid="chat-panel" data-clarity-mask="true" className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[400px] z-[55] h-[min(620px,calc(100vh-2rem))] flex flex-col bg-cream-50 border border-bronze-light/50 rounded-lg shadow-2xl">
      <div className="flex items-center justify-between px-4 py-3 bg-bronze-dark text-white rounded-t-lg">
        <div><div className="font-serif text-lg leading-tight">AI assistant</div><div className="text-[11px] opacity-80" data-testid="chat-ai-disclosure">Automated AI, not a person - not medical advice. Emergency? Call 999.</div></div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close"><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="chat-messages">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div data-testid={`chat-msg-${m.role}-${i}`} dir="auto" className={`max-w-[88%] space-y-2 px-3 py-2 rounded-lg ${m.role === "user" ? "bg-bronze-dark text-white" : "bg-white border border-stone-200"}`}>
              {m.text ? <ChatText text={m.text} /> : <span className="animate-pulse text-ink-muted">…</span>}
              {m.actions?.filter((a) => a.type === "times").map((a, j) => <TimeChoices key={j} idx={i} data={a} onPick={setChoice} />)}
              {m.actions?.some((a) => a.type === "handover") && <p className="text-xs text-bronze-dark" data-testid="chat-handover-note">Passed to our team - they&apos;ll reply as soon as they can.</p>}
            </div>
          </div>
        ))}
        {choice && <ChatBookingForm choice={choice} onCancel={() => setChoice(null)} />}
        <div ref={end} />
      </div>
      {ended ? (
        <p data-testid="chat-ended-note" className="p-3 border-t border-stone-200 text-sm font-medium text-red-700">This chat has ended. In an emergency call 999 or go to A&amp;E now.</p>
      ) : (
      <form onSubmit={send} className="p-3 border-t border-stone-200 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} placeholder="Type your question…" dir="auto"
          className="flex-1 px-3 py-2 text-sm border border-stone-300 rounded-md bg-white focus:outline-none focus:border-bronze-dark" data-testid="chat-input" />
        <button type="submit" disabled={busy || !text.trim()} aria-label="Send" data-testid="chat-send" className="btn-primary px-3 disabled:opacity-40"><PaperPlaneRight size={18} weight="fill" /></button>
      </form>
      )}
      <p data-testid="chat-privacy-note" className="px-3 pb-2 text-[11px] text-ink-muted">Please don&apos;t share medical details. Chats aren&apos;t saved - they disappear when you close this page.</p>
    </div>
  );
};
